/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import { Container, Text } from '@audio-network/componentlibrary'

import { Search } from '@audio-network/componentlibrary/icons'
import StreamingServiceIcon from '../StreamingServiceIcon/StreamingServiceIcon'
import clsx from 'clsx'
import styles from './SearchBox.module.css'
import { useAutocomplete } from './SearchBox.hooks'
import { useTranslation } from 'next-i18next'

const SearchBox = ({ user }: { user: User }): JSX.Element => {
	const { t } = useTranslation(['search'])

	const {
		activeDescendant,
		canAutocomplete,
		handleChange,
		handleBlur,
		handleKeyDown,
		handleOptionClick,
		handleSubmit,
		iconName,
		inputRef,
		listboxId,
		options,
	} = useAutocomplete()

	const showListBox = options?.length > 0 && canAutocomplete.current

	return (
		<Container maxWidth="large">
			<form className={styles.form} onSubmit={handleSubmit}>
				{iconName && (
					<StreamingServiceIcon className={styles.icon} service={iconName} />
				)}

				<input
					aria-activedescendant={
						activeDescendant && `${activeDescendant}-listbox-option`
					}
					aria-controls={listboxId}
					aria-expanded={showListBox}
					aria-label={user ? t('placeholder') : t('search')}
					autoComplete="off"
					// eslint-disable-next-line jsx-a11y/no-autofocus
					autoFocus
					className={clsx(
						styles.input,
						iconName && styles.inputWithIcon,
						user && styles.hideStaticPlaceholderTextOnDesktop
					)}
					data-testid="search-box"
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					onBlur={handleBlur}
					placeholder={user ? t('placeholder') : t('search')}
					ref={inputRef}
					role="combobox"
					type="search"
				/>

				{/* Floating container for placeholder text animation */}
				{user && (
					<Container
						aria-hidden
						className={styles.animatedPlaceholderContainer}
						maxWidth="large"
					>
						<Text align="left" color="ui3" variant="bodyL">
							{t('animated-placeholder')} {''}
							<Text display="inline-block">
								<Text className={styles.animatedPlaceholderText}>
									{t('animated-placeholder-prompt')}
								</Text>
								<Text className={styles.animatedPlaceholderText}>
									{t('animated-placeholder-scene')}
								</Text>
								<Text className={styles.animatedPlaceholderText}>
									{t('animated-placeholder-phase')}
								</Text>
							</Text>
						</Text>
					</Container>
				)}

				<button
					aria-label={t('search-button')}
					className={styles.button}
					data-testid="search-box-button"
					onClick={handleSubmit}
				>
					<Search color="textPrimary" />
				</button>
				<div className={styles.listbox} id={listboxId} role="listbox">
					{showListBox && (
						<ul>
							{options.map((option) => (
								<li key={option.value as string} role="presentation">
									<button
										aria-selected={activeDescendant === option.value}
										className={styles.option}
										data-value={option.value}
										id={`${option.value}-listbox-option`}
										onClick={handleOptionClick}
										role="option"
									>
										{option.label}
									</button>
								</li>
							))}
						</ul>
					)}
				</div>
			</form>
		</Container>
	)
}

export default SearchBox
